import type { GameModel, ReadOnlyGameModel } from 'yage/game/GameModel';
import { contractAirport, readAirline, signRefusal, offers, TERM_SHIFTS, type AirlineState, type Contract } from '../domain/contracts.js';
import { LOUNGE, PASSPORT, stageServing } from '../domain/config.js';
import { deposit } from '../domain/economy.js';
import { state, stations } from './world.js';

/** Stands and contracts of the current airport (fresh defaults until something is signed or upgraded). */
export function airlineOf(g: GameModel | ReadOnlyGameModel): AirlineState { return readAirline(state(g).airline, state(g)); }
export function saveAirline(g: GameModel, a: AirlineState) { state(g).airline = JSON.stringify(a); state(g).revision++; }
export function contractAt(g: GameModel | ReadOnlyGameModel, gate: number): Contract | undefined {
    const e = state(g);
    return contractAirport(e) && e.airline ? airlineOf(g).contracts.find(c => c.gate === gate) : undefined;
}
/** Why this offer cannot be signed at this gate, or null. */
export function refusal(g: GameModel | ReadOnlyGameModel, offerIndex: number, gate: number): string | null {
    const e = state(g), ss = stations(g), a = airlineOf(g), o = offers(e, e.shift, a)[offerIndex];
    if (!o) return 'That offer is gone.';
    return signRefusal(o, gate, a, e, !!ss[PASSPORT]?.unlocked, stageServing(LOUNGE, ss[LOUNGE]));
}
export function sign(g: GameModel, offerIndex: number, gate: number): boolean {
    if (refusal(g, offerIndex, gate)) return false;
    const e = state(g), a = airlineOf(g), o = offers(e, e.shift, a)[offerIndex];
    a.contracts.push({ ...o, gate, termLeft: TERM_SHIFTS, departures: 0, onTime: 0 });
    a.renew = a.renew.filter(name => name !== o.airline);
    saveAirline(g, a);
    return true;
}
/** A departure from a contract gate pays its fee once onto the boarding desk floor (MGT-015). */
export function contractDeparture(g: GameModel, gate: number, onTime: boolean): number {
    const c = contractAt(g, gate);
    if (!c) return 0;
    const a = airlineOf(g), live = a.contracts.find(x => x.gate === gate)!;
    live.departures++; if (onTime) live.onTime++;
    saveAirline(g, a);
    return deposit(state(g), stations(g)[6], c.fee);
}
/** Shift end: count terms down; an expired contract pays a bonus when it met its on-time target, then offers renewal. */
export function settleContracts(g: GameModel): { bonus: number; expired: string[] } {
    const e = state(g);
    if (!contractAirport(e) || !e.airline) return { bonus: 0, expired: [] };
    const a = airlineOf(g), expired: string[] = [];
    let bonus = 0;
    for (const c of a.contracts) {
        c.termLeft--;
        if (c.termLeft > 0) continue;
        if (c.departures > 0 && c.onTime * 100 >= c.target * c.departures) bonus += deposit(e, stations(g)[6], c.fee * 2);
        expired.push(c.airline);
    }
    a.contracts = a.contracts.filter(c => c.termLeft > 0);
    a.renew = [...expired, ...a.renew].slice(0, 8);
    saveAirline(g, a);
    return { bonus, expired };
}
